import classNames from "classnames";
import { Fragment, PropsWithChildren, ReactNode } from "react";
import { UseListFiltersReturn } from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import SvgNavigateNext from "../icons/NagivateNext";
import SvgNavigateBefore from "../icons/NavigateBefore";
import Button from "./Button";
import ErrorText from "./ErrorText";
import ListItemMenu, { ListItemMenuAction } from "./ListItemMenu";
import LoadingSpinner from "./LoadingSpinner";
import SearchBar from "./SearchBar";
import styles from "./Table.module.css";

interface Props<T> {
  className?: string;
  filters?: UseListFiltersReturn;
  searchOptions?: {
    disabled?: boolean;
    placeholder?: string;
    onChange: () => void;
  };
  total?: number;
  head?: ReactNode;
  items?: T[];
  renderRow: (item: T) => ReactNode;
  rowActions?: ListItemMenuAction<T>[];
  footer?: ReactNode;
  isTall?: boolean;
  buttons?: {
    main: ReactNode | ReactNode[];
  };
  isLoading?: boolean;
  error?: boolean;
  forcePagination?: {
    prevPage: {
      action: () => void;
      title: string;
    };
    nextPage: {
      action: () => void;
      title: string;
    };
  };
}

const Table = <T,>({
  className,
  filters,
  searchOptions,
  total = -1,
  head,
  items,
  renderRow,
  rowActions,
  footer,
  isTall = false,
  buttons,
  error,
  isLoading,
  forcePagination,
}: Props<T>) => {
  const i18n = useI18n();

  const isSearchable = filters && filters.isSearchable;
  const page = filters?.state.page ?? 1;
  const limit = filters?.state.limit ?? 5;

  const fromItem = (page - 1) * limit + 1;
  const maxToItem = fromItem + limit - 1;
  const toItem = Math.min(maxToItem, total);

  // const [data, setData] = useState<{ items: T[]; total: number }>();

  // useEffect(() => {
  //   !!items && setData({ items, total });
  // }, [items]);

  // const toItem = Math.max(Math.min(maxToItem, total ?? 0), 0);

  return (
    <>
      {(isSearchable || buttons) && (
        <div className={styles.heading}>
          {buttons?.main
            ? (Array.isArray(buttons.main) ? buttons.main : [buttons.main]).map((x, i) => (
                <Fragment key={`button_${i}`}>{x}</Fragment>
              ))
            : undefined}
          {/* <div className={styles.searchWrapper}> */}
          {isSearchable && (
            <SearchBar
              className={styles.search}
              placeholder={searchOptions?.placeholder ?? i18n.translation.common.search}
              value={filters.state.q ?? ""}
              onChange={(term) => {
                if (searchOptions && !searchOptions?.disabled) {
                  searchOptions.onChange();
                  filters.setSearchTerm(term);
                }
              }}
            />
          )}
          {/* </div> */}
        </div>
      )}

      <table
        className={classNames(className, styles.table, {
          [styles.tall]: isTall,
        })}
      >
        {head && <thead>{head}</thead>}
        {!error && !isLoading && (
          <>
            <tbody>
              {items?.map((item, index) => {
                return (
                  <tr key={`table_row_${index}`}>
                    {renderRow?.(item)}
                    {rowActions?.length && (
                      <td width={1}>
                        <ListItemMenu item={item} actions={rowActions} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {footer && <tfoot>{footer}</tfoot>}
          </>
        )}
      </table>

      {!error && !isLoading && total === 0 && (
        <div className={styles.noItemsFound}>{i18n.translation.common.noItemsFound}</div>
      )}

      {isLoading && (
        <div className={styles.loadingSpinner}>
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className={styles.errorLoadingData}>
          <ErrorText text={i18n.translation.common.errorLoadingData} />
        </div>
      )}

      {!error && !isLoading && total > 0 && (
        <div className={styles.controls}>
          <div>
            {fromItem} - {toItem} of {total}
          </div>
          {total > limit && (
            <div className={styles.pagination}>
              <Button
                glyph={SvgNavigateBefore}
                buttonProps={{
                  title: i18n.translation.common.previousNRows(limit),
                  disabled: page === 1,
                  onClick: () => filters?.prevPage(),
                  className: styles.paginationButton,
                }}
              />
              <Button
                glyph={SvgNavigateNext}
                buttonProps={{
                  title: i18n.translation.common.nextNRows(limit),
                  disabled: toItem === total,
                  onClick: () => filters?.nextPage(),
                  className: styles.paginationButton,
                }}
              />
            </div>
          )}
        </div>
      )}

      {!isLoading && forcePagination && (
        <div className={styles.controls}>
          <div className={styles.pagination}>
            <Button
              glyph={SvgNavigateBefore}
              buttonProps={{
                title: forcePagination.prevPage.title,
                onClick: forcePagination.prevPage.action,
                className: styles.paginationButton,
              }}
            />
            <Button
              glyph={SvgNavigateNext}
              buttonProps={{
                title: forcePagination.nextPage.title,
                onClick: forcePagination.nextPage.action,
                className: styles.paginationButton,
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

const IconTd = ({ children }: PropsWithChildren) => (
  <td className={styles.iconTd}>
    <div>{children}</div>
  </td>
);

Table.IconTd = IconTd;

type TdProps = JSX.IntrinsicElements["td"] &
  PropsWithChildren<{
    hasContextMenu?: boolean;
  }>;

const Td = ({ children, className, hasContextMenu, ...rest }: TdProps) => {
  return (
    <td {...rest} className={classNames(className, { [styles.contextMenu]: hasContextMenu })}>
      {children}
    </td>
  );
};

Table.Td = Td;

export default Table;
